import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import DashboardLayout from "@/components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { userAPI } from "@/lib/api";
import { User, Mail, Shield, Calendar, FileText, Lock, AlertTriangle, Trash2, Crown, Zap, Star } from "lucide-react";
import { toast } from "sonner";

const PLAN_INFO = {
  free: { label: "Free", icon: Star, color: "#64748B", desc: "Basic access with limited usage" },
  pro: { label: "Pro", icon: Zap, color: "#003087", desc: "10 SI, 5 Coaching, 5 CV rewrites per billing cycle" },
  premium: { label: "Premium", icon: Crown, color: "#00A3A6", desc: "Full access to all features" },
};

const SERVICE_LABELS = {
  ats: "ATS Analyses",
  nhs_gen: "NHS Statements",
  coaching: "Coaching Plans",
  cv_rewrite: "CV Rewrites",
  interview_sim: "Interview Sims",
};

export default function SettingsPage() {
  const { user, logout, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [name, setName] = useState("");
  const [savingName, setSavingName] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [savingPassword, setSavingPassword] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState("");

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    try {
      const res = await userAPI.profile();
      setProfile(res.data);
      setName(res.data.name || "");
    } catch {
      toast.error("Failed to load profile");
    }
  };

  const handleUpdateName = async () => {
    if (!name.trim()) {
      toast.error("Name cannot be empty");
      return;
    }
    setSavingName(true);
    try {
      await userAPI.updateProfile({ name: name.trim() });
      toast.success("Name updated");
      await checkAuth();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update name");
    } finally {
      setSavingName(false);
    }
  };

  const handleChangePassword = async () => {
    if (newPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    setSavingPassword(true);
    try {
      await userAPI.changePassword({ current_password: currentPassword, new_password: newPassword });
      toast.success("Password changed successfully");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to change password");
    } finally {
      setSavingPassword(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirm !== "DELETE") return;
    try {
      await userAPI.deleteAccount();
      toast.success("Account deleted");
      await logout();
      navigate("/");
    } catch {
      toast.error("Failed to delete account");
    }
  };

  const plan = PLAN_INFO[user?.subscription_plan] || PLAN_INFO.free;

  return (
    <DashboardLayout>
      <div className="space-y-6 max-w-3xl" data-testid="settings-page">
        <div>
          <h1 className="text-2xl font-bold text-[#003087]" style={{ fontFamily: "Public Sans" }}>Settings</h1>
          <p className="text-slate-500 mt-1">Manage your account, subscription, and preferences.</p>
        </div>

        {/* Profile Info */}
        <Card className="bg-white border border-slate-200 shadow-sm rounded-xl" data-testid="profile-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2" style={{ fontFamily: "Public Sans" }}>
              <User className="w-4 h-4 text-[#003087]" /> Profile
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-full bg-[#003087] flex items-center justify-center text-white text-xl font-semibold">
                {user?.name?.charAt(0)?.toUpperCase() || "U"}
              </div>
              <div className="flex-1">
                <p className="text-slate-900 font-medium">{user?.name}</p>
                <p className="text-sm text-slate-500 flex items-center gap-1.5">
                  <Mail className="w-3.5 h-3.5" /> {user?.email}
                </p>
                {profile?.created_at && (
                  <p className="text-xs text-slate-400 flex items-center gap-1.5 mt-0.5">
                    <Calendar className="w-3 h-3" /> Joined {new Date(profile.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}
                  </p>
                )}
              </div>
            </div>
            <Separator />
            <div className="space-y-2">
              <Label className="text-slate-700">Display Name</Label>
              <div className="flex gap-2">
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Your name"
                  data-testid="settings-name-input"
                />
                <Button
                  onClick={handleUpdateName}
                  disabled={savingName || name === profile?.name}
                  className="bg-[#003087] text-white hover:bg-[#002366] whitespace-nowrap"
                  data-testid="settings-save-name-btn"
                >
                  {savingName ? "Saving..." : "Save"}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Subscription */}
        <Card className="bg-white border border-slate-200 shadow-sm rounded-xl" data-testid="subscription-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2" style={{ fontFamily: "Public Sans" }}>
              <plan.icon className="w-4 h-4" style={{ color: plan.color }} /> Subscription
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-lg font-semibold text-slate-900">{plan.label} Plan</span>
                  <Badge style={{ backgroundColor: `${plan.color}15`, color: plan.color }}>{plan.label}</Badge>
                </div>
                <p className="text-sm text-slate-500">{plan.desc}</p>
              </div>
              {user?.subscription_plan !== "premium" && (
                <Button
                  onClick={() => navigate("/pricing")}
                  className="bg-[#003087] text-white hover:bg-[#002366]"
                  data-testid="settings-upgrade-btn"
                >
                  Upgrade
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Usage Stats */}
        {profile?.service_counts && (
          <Card className="bg-white border border-slate-200 shadow-sm rounded-xl" data-testid="usage-stats-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2" style={{ fontFamily: "Public Sans" }}>
                <FileText className="w-4 h-4 text-[#00A3A6]" /> All-Time Usage
              </CardTitle>
              <CardDescription>{profile.total_outputs} total documents generated</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                {Object.entries(SERVICE_LABELS).map(([key, label]) => (
                  <div key={key} className="text-center p-3 bg-slate-50 rounded-lg">
                    <p className="text-xl font-bold text-slate-900">{profile.service_counts?.[key] || 0}</p>
                    <p className="text-xs text-slate-500">{label}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Change Password */}
        <Card className="bg-white border border-slate-200 shadow-sm rounded-xl" data-testid="password-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2" style={{ fontFamily: "Public Sans" }}>
              <Lock className="w-4 h-4 text-slate-600" /> Change Password
            </CardTitle>
            <CardDescription>Update your account password. Only available for email/password accounts.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="space-y-2">
              <Label className="text-slate-700">Current Password</Label>
              <Input
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder="Enter current password"
                data-testid="settings-current-password"
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="text-slate-700">New Password</Label>
                <Input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Min 6 characters"
                  data-testid="settings-new-password"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-700">Confirm New Password</Label>
                <Input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Repeat new password"
                  data-testid="settings-confirm-password"
                />
              </div>
            </div>
            <Button
              onClick={handleChangePassword}
              disabled={savingPassword || !currentPassword || !newPassword}
              variant="outline"
              className="border-[#003087] text-[#003087]"
              data-testid="settings-change-password-btn"
            >
              {savingPassword ? "Changing..." : "Change Password"}
            </Button>
          </CardContent>
        </Card>

        {/* Danger Zone */}
        <Card className="bg-white border border-red-200 shadow-sm rounded-xl" data-testid="danger-zone-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2 text-red-600" style={{ fontFamily: "Public Sans" }}>
              <AlertTriangle className="w-4 h-4" /> Danger Zone
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-900">Delete Account</p>
                <p className="text-xs text-slate-500">Permanently delete your account and all associated data. This cannot be undone.</p>
              </div>
              <Button
                variant="outline"
                className="border-red-300 text-red-600 hover:bg-red-50"
                onClick={() => setShowDeleteDialog(true)}
                data-testid="settings-delete-btn"
              >
                <Trash2 className="w-4 h-4 mr-1" /> Delete
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Delete Confirmation Dialog */}
        <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <DialogContent className="max-w-sm" data-testid="delete-confirm-dialog">
            <DialogHeader>
              <DialogTitle className="text-red-600 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5" /> Delete Account
              </DialogTitle>
              <DialogDescription>
                This will permanently delete your account, all generated documents, and subscription data. This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 pt-2">
              <div className="space-y-1.5">
                <Label className="text-sm text-slate-700">Type DELETE to confirm</Label>
                <Input
                  value={deleteConfirm}
                  onChange={(e) => setDeleteConfirm(e.target.value)}
                  placeholder="DELETE"
                  data-testid="delete-confirm-input"
                />
              </div>
              <div className="flex gap-2">
                <Button variant="outline" className="flex-1" onClick={() => { setShowDeleteDialog(false); setDeleteConfirm(""); }}>Cancel</Button>
                <Button
                  className="flex-1 bg-red-600 text-white hover:bg-red-700"
                  disabled={deleteConfirm !== "DELETE"}
                  onClick={handleDeleteAccount}
                  data-testid="delete-confirm-btn"
                >
                  Delete Account
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
